/**
 * Push helper: creates the array bucket if missing, then appends.
 */
const pushToGroup = <T>(m: Map<string, T[]>, key: string, item: T) => {
  const bucket = m.get(key);
  if (bucket) bucket.push(item);
  else m.set(key, [item]);
};

/**
 * Generic group-by with a key selector.
 * - Preserves input order inside each group.
 * - By default, empty/undefined keys are allowed (to keep current behavior).
 */
export const groupByKey = <T>(
  arr: readonly T[],
  getKey: (item: T) => string | undefined | null,
  opts: { skipEmpty?: boolean } = {}
): Map<string, T[]> => {
  const { skipEmpty = false } = opts;
  const m = new Map<string, T[]>();
  for (const item of arr) {
    const k = getKey(item);
    if ((k === undefined || k === null || k === '') && skipEmpty) continue;
    // Coerce to string to be safe if a non-empty non-string sneaks in
    pushToGroup(m, String(k ?? ''), item);
  }
  return m;
};

/**
 * Reference-detection specific: group by `sourceDocument`.
 * Kept as a thin wrapper to avoid touching call sites.
 */
export const groupBySource = <T extends { sourceItem: string }>(
  arr: readonly T[],
  opts?: { skipEmpty?: boolean }
): Map<string, T[]> => groupByKey(arr, (x) => x.sourceItem, opts);

/**
 * Inline-block detection specific: group by `item` (document id).
 * Kept as a thin wrapper to avoid touching call sites.
 */
export const groupByItem = <T extends { sourceItem: string }>(
  arr: readonly T[],
  opts?: { skipEmpty?: boolean }
): Map<string, T[]> => groupByKey(arr, (x) => x.sourceItem, opts);

/**
 * Convert a Map<string, V[]> to a plain record.
 * Useful for React rendering keyed by document id.
 */
export const mapToObject = <V>(m: ReadonlyMap<string, V[]>): Record<string, V[]> => {
  return Object.fromEntries(m);
};
